import { appConfig } from '@/config/app-config';
/* eslint-disable @typescript-eslint/no-explicit-any */
import BPCApiRouteHelper from '@core/helpers/bpc-api-route.helper';
import BPCApiServiceInstance from '@core/services/bpc-api-service.instance';
import PosAccessControlService from './pos-access-control-service';
import {
  Barcode,
  POSActivitySession,
  POSBadge,
  POSContact,
  POSDiscountFormModel,
  POSPayment,
  POSPaymentMethod,
  POSPaymentTerminal,
  POSProductCategory,
  POSProductCategoryGroup,
  POSProductListForContact,
  POSProductType,
  POSSession,
  POSTicket,
  POSTicketDetail,
} from '@pos/interfaces/pos-v2';
import { Channel } from 'laravel-echo/dist/channel';
import BPCEchoService from '@core/services/bpc-echo.service';

export default class PosService {
  protected $routeHelper: BPCApiRouteHelper;
  protected $bpcApi: BPCApiServiceInstance;
  protected $bpcApiV1: BPCApiServiceInstance;
  protected $bpcEchoService: BPCEchoService;
  protected accessControlService: PosAccessControlService;
  constructor() {
    this.$routeHelper = new BPCApiRouteHelper();
    this.$bpcApi = new BPCApiServiceInstance({ apiPath: appConfig.apiPathV2 });
    this.$bpcApiV1 = new BPCApiServiceInstance({ apiPath: appConfig.apiPathV1 });
  }

  get $bpcEcho(): BPCEchoService {
    if (!this.$bpcEchoService) {
      this.$bpcEchoService = new BPCEchoService();
    }
    return this.$bpcEchoService;
  }

  /**
   * Access control service
   * @returns {PosAccessControlService}
   * @readonly
   * @memberof PosService
   * @example this.$posService.accessControl
   * @example this.$posService.accessControl.getAccessControlEvents(1)
   */
  get accessControl(): PosAccessControlService {
    if (!this.accessControlService) {
      return new PosAccessControlService();
    } else {
      return this.accessControlService;
    }
  }

  async getOpenedTickets(PosSessionId: number): Promise<POSTicket[]> {
    const path = this.$routeHelper.getApiPath(`pos/${PosSessionId}/tickets`);
    const response = await this.$bpcApi.get(path).toPromise();
    const result: POSTicket[] = response.data;
    return result;
  }

  async getContactProducts(
    posSessionId: number,
    contactId: number,
  ): Promise<POSProductListForContact[]> {
    const path = this.$routeHelper.getApiPath(`pos/${posSessionId}/contacts/${contactId}/products`);
    const response = await this.$bpcApi.get(path).toPromise();
    const result: POSProductListForContact[] = response.data;
    return result;
  }

  async getTicketDetail(posSessionId: number, ticketId: number): Promise<POSTicketDetail> {
    const path = this.$routeHelper.getApiPath(`pos/${posSessionId}/tickets/${ticketId}`);
    const response = await this.$bpcApi.get(path).toPromise();
    const result: POSTicketDetail = response.data;
    return result;
  }

  async reopenTicket(posSessionId: number, ticketId: number): Promise<POSTicketDetail> {
    const path = this.$routeHelper.getApiPath(`pos/${posSessionId}/tickets/${ticketId}/reopen`);
    const response = await this.$bpcApi.post(path).toPromise();
    const result: POSTicketDetail = response.data;
    return result;
  }

  async addNewTicket(posSessionId: number): Promise<POSTicket> {
    const path = this.$routeHelper.getApiPath(`pos/${posSessionId}/tickets`);
    const response = await this.$bpcApi.post(path).toPromise();
    const result: POSTicket = response.data;
    return result;
  }

  async getPaymentMethodOptions(PosSessionId: number): Promise<POSPaymentMethod[]> {
    const path = this.$routeHelper.getApiPath(`pos/${PosSessionId}/payment-methods`);
    const response = await this.$bpcApi.get(path).toPromise();
    const result: POSPaymentMethod[] = response.data;
    return result;
  }

  async getProductCategoryGroups(posSessionId: number): Promise<POSProductCategoryGroup[]> {
    const path = this.$routeHelper.getApiPath(`pos/${posSessionId}/product-categories`);
    const response = await this.$bpcApi.get(path).toPromise();
    const result: POSProductCategoryGroup[] = response.data;
    return result;
  }

  async getTicketBarcodes(posSessionId: number, ticketId: number): Promise<Barcode[]> {
    const path = this.$routeHelper.getApiPath(`pos/${posSessionId}/tickets/${ticketId}/barcodes`);
    const response = await this.$bpcApi.get(path).toPromise();
    const result: Barcode[] = response.data;
    return result;
  }

  async validateSepaData(iban: string, bic: string): Promise<any> {
    const path = this.$routeHelper.getApiPath(`mandates/validate-sepa-data`);
    const response = await this.$bpcApiV1
      .get(path, {
        params: {
          iban: iban,
          bic: bic,
        },
      })
      .toPromise();

    const result = response.data;
    return result;
  }

  async addProductToTicket(
    posSessionId: number,
    ticketId: number,
    productCategory: POSProductCategory,
    quantity: number,
    extraData: object = {},
  ): Promise<POSTicketDetail> {
    const path = this.$routeHelper.getApiPath(
      `pos/${posSessionId}/tickets/${ticketId}/${productCategory.productType}/${productCategory.id}`,
    );
    const body = {
      quantity: quantity,
      additionalParameters: extraData,
    };
    const response = await this.$bpcApi.post(path, body).toPromise();
    const result: POSTicketDetail = response.data;
    return result;
  }

  async createAccessControlEvent(
    posSessionId: number,
    quantity: number,
    resourceId: number,
    isIn: boolean, // true if the passage is an entrance, false if it's an exit
    comment: string,
  ): Promise<void> {
    const path = this.$routeHelper.getApiPath(`pos/${posSessionId}/access-control-events/`);
    const body = {
      resourceId: resourceId,
      quantity: quantity,
      in: isIn,
      comment: comment,
    };
    const response = await this.$bpcApi.post(path, body).toPromise();
    const result = response.data;
    return result;
  }

  async closeTicket(posSessionId: number, ticketId: number): Promise<POSTicketDetail> {
    const path = this.$routeHelper.getApiPath(`pos/${posSessionId}/tickets/${ticketId}/close`);
    const response = await this.$bpcApi.post(path).toPromise();
    const result: POSTicketDetail = response.data;
    return result;
  }

  async markAsPaidTicket(posSessionId: number, ticketId: number): Promise<POSTicketDetail> {
    const path = this.$routeHelper.getApiPath(
      `pos/${posSessionId}/tickets/${ticketId}/mark-as-paid`,
    );
    const response = await this.$bpcApi.post(path).toPromise();
    return response.data;
  }

  async searchContact(posSessionId: number, search: string): Promise<POSContact[]> {
    const path = this.$routeHelper.getApiPath(`pos/${posSessionId}/contacts`);
    const config = {
      params: {
        search,
      },
    };
    const response = await this.$bpcApi.get(path, config).toPromise();
    const result: POSContact[] = response.data;
    return result;
  }

  async changeContact(
    posSessionId: number,
    ticketId: number,
    contactId: number | null,
  ): Promise<POSTicketDetail> {
    const path = this.$routeHelper.getApiPath(`pos/${posSessionId}/tickets/${ticketId}`);
    const body = {
      contactId: contactId,
    };
    const response = await this.$bpcApi.patch(path, body).toPromise();
    const result: POSTicketDetail = response.data;
    return result;
  }

  async addContactBadge(
    posSessionId: number,
    contactId: number | null,
    badgeNumber: string,
  ): Promise<POSContact> {
    const path = this.$routeHelper.getApiPath(`pos/${posSessionId}/contacts/${contactId}/badges`);
    const body = {
      badgeNumber,
    };
    const response = await this.$bpcApi.post(path, body).toPromise();
    const result: POSContact = response.data;
    return result;
  }

  async getBadgesOfContact(contactId: number | null): Promise<POSBadge[]> {
    const path = this.$routeHelper.getApiPath(`contacts/${contactId}/badges`);
    const response = await this.$bpcApiV1.get(path).toPromise();
    const result: POSBadge[] = response.data;
    return result;
  }

  async getPrimaryBadgeByContact(contactId: number | null): Promise<POSBadge> {
    const path = this.$routeHelper.getApiPath(`contacts/${contactId}/get-primary-badge`);
    const response = await this.$bpcApiV1.get(path).toPromise();
    const result: POSBadge = response.data;
    return result;
  }

  async changeProductQuantity(
    posSessionId: number,
    ticketId: number,
    productType: POSProductType,
    productId: number,
    quantity: number,
  ): Promise<POSTicketDetail> {
    const path = this.$routeHelper.getApiPath(
      `pos/${posSessionId}/tickets/${ticketId}/products/${productId}`,
    );
    const body = {
      quantity: quantity,
    };
    const response = await this.$bpcApi.patch(path, body).toPromise();
    const result: POSTicketDetail = response.data;
    return result;
  }

  async changeProductOwner(
    posSessionId: number,
    ticketId: number,
    productType: POSProductType,
    productId: number,
    contactId: number,
    badgeNumber: string,
  ): Promise<POSTicketDetail> {
    const path = this.$routeHelper.getApiPath(
      `pos/${posSessionId}/tickets/${ticketId}/products/${productId}/patchOwner`,
    );
    const body = {
      contactId: contactId,
      badgeNumber: badgeNumber,
    };
    const response = await this.$bpcApi.patch(path, body).toPromise();
    const result: POSTicketDetail = response.data;
    return result;
  }

  async changeBadgeForProduct(
    posSessionId: number,
    ticketId: number,
    productType: POSProductType,
    productId: number,
    contactId: number,
    badgeNumber: string,
    badgeType: string,
  ): Promise<POSTicketDetail> {
    const path = this.$routeHelper.getApiPath(
      `pos/${posSessionId}/tickets/${ticketId}/products/${productId}/patchBadge`,
    );
    const body = {
      contactId: contactId,
      badgeNumber: badgeNumber,
      badgeType: badgeType,
    };
    const response = await this.$bpcApi.patch(path, body).toPromise();
    const result: POSTicketDetail = response.data;
    return result;
  }

  async changeSubscriptionMandateData(
    posSessionId: number,
    ticketId: number,
    productId: number,
    mandateId: number,
    contactId: number,
    ibanNumber: string,
    bicNumber: string,
  ): Promise<POSTicketDetail> {
    const path = this.$routeHelper.getApiPath(
      `pos/${posSessionId}/tickets/${ticketId}/products/${productId}/patch-mandate`,
    );
    const body = {
      mandateId: mandateId,
      contactId: contactId,
      bicNumber: bicNumber,
      ibanNumber: ibanNumber,
    };
    const response = await this.$bpcApi.patch(path, body).toPromise();
    return response.data;
  }

  async getBadgeForProduct(
    posSessionId: number,
    ticketId: number,
    productId: number,
  ): Promise<string | undefined> {
    const path = this.$routeHelper.getApiPath(
      `pos/${posSessionId}/tickets/${ticketId}/products/${productId}/badges`,
    );
    const response = await this.$bpcApi.get(path).toPromise();
    const badgeNumber: string | undefined = response.data;
    return badgeNumber;
  }

  async removeProduct(
    posSessionId: number,
    ticketId: number,
    productType: POSProductType,
    productId: number,
  ): Promise<POSTicketDetail> {
    console.log('remove product type', productType);
    const path = this.$routeHelper.getApiPath(
      `pos/${posSessionId}/tickets/${ticketId}/products/${productId}`,
    );
    const response = await this.$bpcApi.delete(path).toPromise();
    const result: POSTicketDetail = response.data;
    return result;
  }

  async cancelProductFromTicket(
    posSessionId: number,
    ticketId: number,
    productId: number,
  ): Promise<number[]> {
    const path = this.$routeHelper.getApiPath(
      `pos/${posSessionId}/tickets/${ticketId}/products/${productId}/cancel`,
    );
    const response = await this.$bpcApi.post(path).toPromise();
    return response.data;
  }

  async cancelProductFromProduct(
    posSessionId: number,
    productType: string,
    productId: number,
  ): Promise<number[]> {
    const path = this.$routeHelper.getApiPath(
      `pos/${posSessionId}/products/${productType}/${productId}/cancel`,
    );
    const response = await this.$bpcApi.post(path).toPromise();
    return response.data;
  }

  async incrementCredits(
    posSessionId: number,
    productType: string,
    productId: number,
    increment: number,
  ): Promise<number[]> {
    const path = this.$routeHelper.getApiPath(
      `pos/${posSessionId}/products/${productType}/${productId}/increment-credits`,
    );
    const body = {
      increment: increment,
    };
    const response = await this.$bpcApi.post(path, body).toPromise();
    return response.data;
  }

  async checkoutToTicket(
    posSessionId: number,
    orderIds: number[],
    contactId: number,
    ticketId?: number,
  ): Promise<POSTicket> {
    const path = this.$routeHelper.getApiPath(`pos/${posSessionId}/tickets/checkout`);
    const body = {
      posSessionId: posSessionId,
      orderIds: orderIds,
      contactId: contactId,
      ticketId: ticketId,
    };
    const response = await this.$bpcApi.post(path, body).toPromise();
    return response.data;
  }

  async getPosSession(posSessionId: number | null = null): Promise<POSSession> {
    if (posSessionId) {
      const res = await this.$bpcApi
        .get(this.$routeHelper.getApiPath(`pos/pos-sessions/${posSessionId}`))
        .toPromise();
      return res.data as POSSession;
    } else {
      const res = await this.$bpcApi
        .get(this.$routeHelper.getApiPath(`pos/pos-sessions/current`))
        .toPromise();
      return res.data as POSSession;
    }
  }

  async createPayment(
    posSessionId: number,
    ticketId: number,
    amount: number,
    paymentMethod: string,
    contactId: number,
    terminalId?: number,
  ): Promise<POSTicketDetail> {
    const path = this.$routeHelper.getApiPath(`pos/${posSessionId}/tickets/${ticketId}/payments`);
    // TODO change this to camelCase if we replace old POS
    // and change the DTO in backend
    const body = {
      invoice_id: ticketId,
      paid_amount: amount,
      payment_method_type: paymentMethod,
      contact_id: contactId,
    };

    if (terminalId) {
      body['terminal_id'] = terminalId;
    }
    const response = await this.$bpcApi.post(path, body).toPromise();
    const result: POSTicketDetail = response.data;
    return result;
  }

  async sendTicketByEmail(
    posSessionId: number,
    ticketId: number,
    email: string,
  ): Promise<POSTicketDetail> {
    const path = this.$routeHelper.getApiPath(
      `pos/${posSessionId}/tickets/${ticketId}/send-ticket-by-email`,
    );
    const body = {
      email: email,
    };
    const response = await this.$bpcApi.post(path, body).toPromise();
    const result: POSTicketDetail = response.data;
    return result;
  }

  async getOngoingPaymentForNeptingTherminal(
    posSessionId: number,
    terminalId: number,
  ): Promise<any> {
    const res = await this.$bpcApi
      .get(
        this.$routeHelper.getApiPath(
          `pos/${posSessionId}/terminals/${terminalId}/nepting-ongoing-payment`,
        ),
      )
      .toPromise();
    return res.data;
  }

  async resumeNeptingPayment(
    posSessionId: number,
    ticketId: number,
    paymentId: number,
  ): Promise<POSTicketDetail> {
    const path = this.$routeHelper.getApiPath(
      `pos/${posSessionId}/tickets/${ticketId}/resume-nepting-payment`,
    );

    const body = {
      payment_id: paymentId,
    };

    const response = await this.$bpcApi.post(path, body).toPromise();
    const result: POSTicketDetail = response.data;
    return result;
  }

  paymentMethodNeedsToWaitForExternalResponse(paymentMethod: string): boolean {
    return ['wipay_terminal', 'electronic_sport_voucher', 'cashdro'].includes(paymentMethod);
  }

  listenPaymentEvents(
    posSessionId: number,
    ticketId: number,
    paymentMethod: string,
  ): Channel | null {
    console.log('listenPaymentEvents', posSessionId, ticketId, paymentMethod);
    if (
      paymentMethod === 'wipay_terminal' ||
      paymentMethod === 'electronic_sport_voucher' ||
      paymentMethod === 'nepting'
    ) {
      return this.$bpcEcho.getTerminalPaymentChannel();
    }
    return;
    // TODO
  }

  leaveTerminalPaymentChannel(posSessionId: number, ticketId: number, paymentMethod: string): void {
    console.log('listenPaymentEvents', posSessionId, ticketId, paymentMethod);
    if (
      paymentMethod === 'wipay_terminal' ||
      paymentMethod === 'electronic_sport_voucher' ||
      paymentMethod === 'nepting'
    ) {
      return this.$bpcEcho.leaveTerminalPaymentChannel();
    }
    return;
  }

  // Activity Sessions and attendees

  async getActivitySessions(
    posSessionId: number,
    date: string,
    contactId: number,
  ): Promise<POSActivitySession[]> {
    const path = this.$routeHelper.getApiPath(
      `pos/${posSessionId}/contacts/${contactId}/activity-sessions`,
    );
    const config = {
      params: {
        date,
      },
    };

    const response = await this.$bpcApi.get(path, config).toPromise();
    const result: POSActivitySession[] = response.data;
    return result;
  }

  // Terminal payments
  async updateTerminalPaymentStatus(
    posSessionId: number,
    ticketId: number,
    paymentId: number,
  ): Promise<POSTicketDetail> {
    const path = this.$routeHelper.getApiPath(
      `pos/${posSessionId}/tickets/${ticketId}/payments/${paymentId}/check-status`,
    );

    const response = await this.$bpcApi.post(path).toPromise();
    const result: POSTicketDetail = response.data;
    return result;
  }

  async updatePaymentMethodType(
    posSessionId: number,
    ticketId: number,
    paymentId: number,
    paymentMethodType: string,
  ): Promise<POSPayment> {
    const path = this.$routeHelper.getApiPath(
      `pos/${posSessionId}/tickets/${ticketId}/payments/${paymentId}/change-method-type`,
    );

    const body = {
      payment_method_type: paymentMethodType,
    };

    const response = await this.$bpcApi.patch(path, body).toPromise();
    return response.data;
  }

  async abortNeptingTerminal(
    posSessionId: number,
    ticketId: number,
    paymentId: number,
  ): Promise<POSTicketDetail> {
    const path = this.$routeHelper.getApiPath(
      `pos/${posSessionId}/tickets/${ticketId}/payments/${paymentId}/abort-nepting-payment`,
    );

    const response = await this.$bpcApi.put(path).toPromise();
    return response.data;
  }

  async forceAcceptNeptingPayment(
    posSessionId: number,
    ticketId: number,
    paymentId: number,
  ): Promise<POSTicketDetail> {
    const path = this.$routeHelper.getApiPath(
      `pos/${posSessionId}/tickets/${ticketId}/payments/${paymentId}/force-accept`,
    );

    const response = await this.$bpcApi.post(path).toPromise();
    return response.data;
  }

  async getAvailableTerminals(
    posSessionId: number,
    terminalType: string,
  ): Promise<POSPaymentTerminal[]> {
    const path = this.$routeHelper.getApiPath(`pos/${posSessionId}/terminals`);
    const config = {
      params: {
        terminal_type: terminalType,
      },
    };
    const response = await this.$bpcApi.get(path, config).toPromise();
    const result: POSPaymentTerminal[] = response.data;
    return result;
  }

  async editPrice(
    posSessionId: number,
    ticketId: number,
    formModel: POSDiscountFormModel,
  ): Promise<POSTicketDetail> {
    const path = this.$routeHelper.getApiPath(`pos/${posSessionId}/tickets/${ticketId}/edit-price`);
    const response = await this.$bpcApi.post(path, formModel).toPromise();
    const result: POSTicketDetail = response.data;
    return result;
  }
  async addOrigin(
    posSessionId: number,
    ticketId: number,
    origin: string,
  ): Promise<POSTicketDetail> {
    const path = this.$routeHelper.getApiPath(`pos/${posSessionId}/tickets/${ticketId}/origin`);
    const response = await this.$bpcApi.patch(path, { origin }).toPromise();
    const result: POSTicketDetail = response.data;
    return result;
  }

  async postCashMovement(
    posSessionId: number,
    type: string,
    amount: number,
    justification: string,
  ): Promise<void> {
    const path = this.$routeHelper.getApiPath(`pos/${posSessionId}/cash-movements`);
    await this.$bpcApi.post(path, { type, amount, justification }).toPromise();
  }

  async getOriginalPrice(
    posSessionId: number,
    ticketId: number,
    productId: number,
  ): Promise<number> {
    const path = this.$routeHelper.getApiPath(
      `pos/${posSessionId}/tickets/${ticketId}/products/${productId}/original-price`,
    );
    const response = await this.$bpcApi.get(path).toPromise();
    return response.data;
  }

  async reopenSession(posSessionId: number, reviewer_profile_id: number): Promise<POSSession> {
    const body = {
      id: posSessionId,
      reviewer_profile_id: reviewer_profile_id,
    };
    const path = this.$routeHelper.getApiPath(`pos/${posSessionId}/reopen`);
    const response = await this.$bpcApi.patch(path, body).toPromise();
    return response.data;
  }

  async createQuickPosSession(
    posBoxId: number,
    beginningCash: number,
    observations: string,
  ): Promise<any> {
    const url = this.$routeHelper.getEntityPath('pos-sessions') + '/quick-create';
    const params = { posBoxId, beginningCash, observations };

    try {
      const res = await this.$bpcApiV1.post(url, params).toPromise();
      return res.data;
    } catch (error) {
      throw error?.response?.data?.errors || error;
    }
  }
}
